import React, { useMemo } from 'react';
import { Tab, Tabs } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EmailSettings from '../company/EmailSettings';
import EmailTemplates from './EmailTemplates';
import PageHeader, { PageBody, PageCard, PageHeaderBackButton, PageShell } from '../../../common/PageHeader';

const TAB_SETTINGS = 'settings';
const TAB_TEMPLATE = 'template';
const FONT = { fontFamily: "'Poppins', sans-serif" };

export default function EmailConfiguration() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo(() => {
    const tab = searchParams.get('tab');
    return tab === TAB_TEMPLATE ? TAB_TEMPLATE : TAB_SETTINGS;
  }, [searchParams]);

  const handleTabChange = (_event, value) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', value);
    setSearchParams(nextParams, { replace: true });
  };

  const header =
    activeTab === TAB_TEMPLATE
      ? {
          title: t('settings.emailTemplates', 'Email Templates'),
          subtitle: t(
            'settings.emailTemplatesSub',
            'Customize and manage email communications for recruitment phases'
          ),
        }
      : {
          title: t('emailSettings.title'),
          subtitle: t('emailSettings.subtitle'),
        };

  return (
    <PageShell className="bg-[#f8fafc]" style={FONT}>
      <PageCard overflow={false}>
        <PageHeader
          title={header.title}
          subtitle={header.subtitle}
          leading={<PageHeaderBackButton onClick={() => navigate(-1)} />}
        />
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            minHeight: 48,
            borderBottom: '1px solid #e5e7eb',
            '& .MuiTab-root': {
              ...FONT,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              minHeight: 48,
              color: '#64748b',
              '&.Mui-selected': { color: '#0ea5e9' },
            },
            '& .MuiTabs-indicator': { backgroundColor: '#0ea5e9', height: 3 },
          }}
        >
          <Tab value={TAB_SETTINGS} label={t('nav.emailSettings')} />
          <Tab value={TAB_TEMPLATE} label={t('nav.emailTemplate')} />
        </Tabs>
        {activeTab === TAB_SETTINGS ? (
          <PageBody className="p-4 sm:p-5">
            <EmailSettings embedded />
          </PageBody>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-3 sm:p-4">
            <EmailTemplates embedded />
          </div>
        )}
      </PageCard>
    </PageShell>
  );
}
